import { RefreshCw } from "lucide-react";
import { useState } from "react";

import { ApiError } from "@/api/client";
import type { Severity } from "@/api/types";
import { AreaCard } from "@/components/AreaCard";
import { DryRunBanner } from "@/components/DryRunBanner";
import { IncidentCharts } from "@/components/IncidentCharts";
import { IncidentTable } from "@/components/IncidentTable";
import { Pagination } from "@/components/Pagination";
import { SeverityFilter } from "@/components/SeverityFilter";
import { StatusFilter, type StatusFilterValue } from "@/components/StatusFilter";
import { Button } from "@/components/ui/button";
import { useIncidents, type IncidentFilters } from "@/hooks/useIncidents";
import { useMyProfile } from "@/hooks/useMe";

const PER_PAGE = 50;

// Mapeo segmento → ruta de la app
const SEGMENT_ROUTES: Record<string, string> = {
  dashboard: "/",
  analysis: "/analysis/new",
  approvals: "/approvals",
  health: "/health",
  dashboards: "/dashboards",
  runs: "/runs",
  reports: "/reports",
  ecc: "/ecc",
  srp: "/srp/catalog",
  usage: "/usage",
  admin_usage: "/admin/usage",
  admin_telegram: "/admin/telegram",
  admin_config: "/admin/configuracion",
};

// Mapeo área → segmento padre
const AREA_SEGMENT: Record<string, string> = {
  nova: "ecc",
  soporte: "ecc",
  crea: "ecc",
  custodias: "ecc",
};

export function DashboardPage() {
  const [apartado, setApartado] = useState<StatusFilterValue>("open");
  const [severities, setSeverities] = useState<Severity[]>([]);
  const [page, setPage] = useState(1);

  // "Estadísticas" es una vista, no un estado de incidente: se traduce a uno
  // válido antes de consultar. La consulta se deja viva a propósito para que al
  // volver a la lista ya esté fresca; son los mismos datos, no una llamada extra.
  const esEstadisticas = apartado === "estadisticas";
  const status: IncidentFilters["status"] = esEstadisticas ? "open" : apartado;

  const filters: IncidentFilters = { status, severities, page, perPage: PER_PAGE };
  const query = useIncidents(filters);
  const profile = useMyProfile();

  const data = query.data;
  const isInitialLoading = query.isPending && !data;
  const errorMessage = query.error
    ? query.error instanceof ApiError
      ? query.error.message
      : "Error al cargar incidentes."
    : null;

  const areaRoles = profile.data?.area_roles ?? [];
  const isSuper = (profile.data?.global_roles ?? []).includes("super_user");

  return (
    <div className="space-y-8">
      {/* ── Mis áreas ───────────────────────────────────────────────── */}
      {areaRoles.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold">Mis áreas</h2>
            {isSuper && (
              <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-400">
                super_user · acceso total
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {areaRoles.map(ar => {
              const segCode = AREA_SEGMENT[ar.area_code];
              const segRoute = segCode ? SEGMENT_ROUTES[segCode] : undefined;
              return (
                <AreaCard
                  key={ar.area_id}
                  areaCode={ar.area_code}
                  areaName={ar.area_name}
                  role={ar.role as "operator" | "manager"}
                  segmentCode={segCode}
                  segmentRoute={segRoute}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* ── Incidentes ──────────────────────────────────────────────── */}
      <section className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Incidentes</h1>
            <p className="text-sm text-muted-foreground">
              Refresco automático cada 30 segundos.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={query.isFetching ? "size-4 animate-spin" : "size-4"} />
            Refrescar
          </Button>
        </div>

        <DryRunBanner />

        <div className="flex flex-wrap items-center gap-4">
          <StatusFilter
            value={apartado}
            onChange={(next) => {
              setApartado(next);
              setPage(1);
            }}
          />
          {/* La severidad filtra la TABLA. En Estadísticas no aplica, y dejarla
              visible haría creer que las gráficas responden a ella. */}
          {!esEstadisticas && (
            <SeverityFilter
              value={severities}
              onChange={(next) => {
                setSeverities(next);
                setPage(1);
              }}
            />
          )}
        </div>

        {errorMessage && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {errorMessage}
          </div>
        )}

        {esEstadisticas ? (
          <IncidentCharts />
        ) : isInitialLoading ? (
          <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
            Cargando incidentes…
          </div>
        ) : data ? (
          <>
            <IncidentTable items={data.items} />
            <Pagination
              page={data.page}
              perPage={data.per_page}
              total={data.total}
              onPageChange={setPage}
            />
          </>
        ) : null}
      </section>
    </div>
  );
}
